/**
 * RWBR — EZRA SUPER-SPEEDWAY DRAFTING (the DS / Draft Event system).
 *
 * SOURCE: `EZRAS2RulesFinal.docx` §3.10, "Drafting Rules". This is EZRA's own
 * Season 2 ruleset and it is the version implemented here.
 *
 * ⚠ THERE ARE TWO INCOMPATIBLE DRAFTING SYSTEMS IN THIS PROJECT'S SOURCES, from
 * the same two authors, and only one can be used:
 *
 *   `RWBR_MH_drafting.pdf`   Mike Haley & Bruce Peckham, Version 3, 17 Aug 2016.
 *                            Trigger: Race Cam die plus an extra d6, draft on a
 *                            6. Drafts of 2–7 cars. Entry gate is a Gas Pedal
 *                            check with rating ≥ 3. Movement 2/3/4/5 places by
 *                            draft size. A whole pre-turn and post-turn phase.
 *
 *   `EZRAS2RulesFinal.docx`  EZRA Season 2 — LATER, and the reference league's
 *                            own rules. Trigger: a driver with a DS trait
 *                            challenging on any quality. −1 to his red die.
 *                            2–3 cars. No d6 gate, no gas-rating floor.
 *
 * `CROSS_REFERENCE.md` C16 records them as mutually exclusive. **EZRA is this
 * project's reference league and its Season 2 document is the later of the two,
 * so EZRA governs (D-EZ-1).** The 2016 sheet is not implemented, and the two
 * must never be merged — a draft cannot be both a 7-car train gated on a d6 and
 * a 2-car move gated on a trait.
 *
 * WHY THIS MATTERS RIGHT NOW. The booth already tells you Talladega is "a pack
 * forty cars deep" with "nowhere to go", while underneath the engine runs the
 * same passing rules it uses at Martinsville. The words are ahead of the rules.
 * This closes that gap.
 *
 * WHERE IT IS CALLED FROM. `draftPhase.ts`, which `turn.ts` runs AFTER the
 * ordinary quality challenges have resolved — because EZRA says "The DS makes
 * his challenge attempt after all other challenges have been completed."
 */

import type { Car, DriverCard, Quality, RaceState } from './types.ts';
import type { TrackCard } from './turn.ts';

export const CITE_DRAFT = 'EZRA-S2 §3.10 — Drafting Rules (DS trait, Draft Event)';
export const CITE_DRAFT_TRIGGER =
  'EZRA-S2 §3.10 — "A draft is triggered on any quality challenge action when the DS driver is the challenger"';
export const CITE_DRAFT_RED_DIE =
  'EZRA-S2 §3.10 — "He receives a -1 to his red die roll for the gas challenge"';
export const CITE_DRAFT_ORDER =
  'EZRA-S2 §3.10 — "The DS makes his challenge attempt after all other challenges have been completed"';
export const CITE_DRAFT_DE =
  'EZRA-S2 §3.10 DE Procedure — "The successful DS driver advances to the spot of the defeated target driver"';
export const CITE_DRAFT_DMD =
  'EZRA-S2 §3.10 DE Procedure — "The first car on the grid behind the DS driver becomes a draft-mated driver (DMD)"';
export const CITE_DRAFT_CANCEL =
  'EZRA-S2 §3.10 — "Any interruption of the turn prior to the DE due to RWBR or EZRA procedure will cancel the DE"';
export const CITE_DRAFT_NO_CARRY = 'EZRA-S2 §3.10 — "Draft effects do not carry over to the next turn"';
export const CITE_DRAFT_STARRED =
  'EZRA-S2 §3.10 — "If the DS has a * at the active challenge quality and can make second challenge in the turn, the DE is repeated"';
export const CITE_DRAFT_CHIPS =
  'EZRA-S2 §3.10 — "Performance chips can be used to either win or defend the challenge that creates the DE, but not the implementation of the advance"';
export const CITE_DRAFT_BOTTOM =
  'EZRA-S2 §3.10 — "The DE can occur on a Bottom Group Churn, but the DS cannot extend beyond the Bottom Group"';
export const CITE_DRAFT_TWO_DS =
  'EZRA-S2 §3.10 — "If two or more DS drivers are in the same DE, three cars will move"';

/**
 * RULING D-EZ-2 — the source sentence is corrupt and had to be read.
 *
 * EZRA-S2 prints, verbatim:
 *
 *   "He receives a -1 to his red die roll for the gas challenge.
 *    **If he fails e**, a Draft Event (DE) is created."
 *
 * "If he fails e" is not a sentence. Read literally it says a DE is created
 * when the DS driver LOSES, which contradicts the very next paragraph — the DE
 * Procedure opens "**The successful DS driver** advances to the spot of the
 * defeated target driver," which cannot happen if he failed.
 *
 * Two further pieces of evidence point the same way. The −1 applies to the red
 * die, and in a gas challenge the red die must come in AT OR UNDER the gas
 * pedal — so the modifier makes him MORE likely to succeed. A rule that rewards
 * failure would not be handed a success bonus. And drafting in the real sport
 * is a mechanism for completing a pass, not for muffing one.
 *
 * **Ruled: the Draft Event fires on a SUCCESSFUL challenge.** The same artifact
 * ("If he fails e") is noted in the project's own intake as a known text-layer
 * corruption in this document.
 */
export const DE_ON_SUCCESS = true;

/** The red-die modifier a DS driver carries into his gas challenge. */
export const DS_RED_DIE_MODIFIER = -1;

/** EZRA-S2: a DE moves two cars; three when a second DS driver is caught up in it. */
export const DE_CARS_NORMAL = 2;
export const DE_CARS_TWO_DS = 3;

/**
 * Drafting happens on super-speedways and nowhere else.
 *
 * Matched on the track card's own printed category rather than on lap length,
 * because the category is what the source names. Talladega and Daytona are
 * Super Speedway; Martinsville is not, and no amount of arithmetic should make
 * it one.
 */
export const isSuperSpeedway = (track: Pick<TrackCard, 'category'>): boolean =>
  track.category === 'Super Speedway';

/**
 * Does this card carry the DS trait?
 *
 * DS is an EZRA TRAIT, not one of the six base qualities (REFLEX, CONTROL,
 * SKILL, STAR, SMART, HEAT). It therefore lives in its own optional field, and
 * a card without it simply never drafts — which is every published card, since
 * the trait is EZRA's own addition.
 */
export const hasDS = (card: Pick<DriverCard, 'trait'>): boolean => card.trait === 'DS';

// ---------------------------------------------------------------------------

export interface DraftEligibility {
  eligible: boolean;
  /** Why not, in words, when it is not. */
  reason: string | null;
  ruleCitation: string;
}

/**
 * May this challenge create a Draft Event?
 *
 * Every gate EZRA names, checked in the order it names them. The reasons are
 * carried as text because a draft that silently does not happen is impossible
 * to debug and impossible to narrate.
 */
export function draftEligible(input: {
  track: Pick<TrackCard, 'category'>;
  challenger: Pick<DriverCard, 'trait'>;
  /** Was the DS driver the CHALLENGER? EZRA requires it; defending never drafts. */
  isChallenger: boolean;
  /** A DE is cancelled by any interruption earlier in the turn. */
  turnInterrupted?: boolean;
}): DraftEligibility {
  if (!isSuperSpeedway(input.track)) {
    return { eligible: false, reason: 'not a super-speedway', ruleCitation: CITE_DRAFT };
  }
  if (!hasDS(input.challenger)) {
    return { eligible: false, reason: 'the driver has no DS trait', ruleCitation: CITE_DRAFT };
  }
  if (!input.isChallenger) {
    return {
      eligible: false,
      reason: 'the DS driver was the target, not the challenger',
      ruleCitation: CITE_DRAFT_TRIGGER,
    };
  }
  if (input.turnInterrupted === true) {
    return {
      eligible: false,
      reason: 'the turn was interrupted before the draft event',
      ruleCitation: CITE_DRAFT_CANCEL,
    };
  }
  return { eligible: true, reason: null, ruleCitation: CITE_DRAFT_TRIGGER };
}

/**
 * The red die a DS driver actually rolls against his gas pedal.
 *
 * Floored at 1: a die cannot show less than one, and a modifier that produced a
 * zero would quietly turn "very likely" into "certain", which is a different
 * rule from the one EZRA wrote.
 */
export const draftRedDie = (raw: number): number => Math.max(1, raw + DS_RED_DIE_MODIFIER);

// ---------------------------------------------------------------------------
// The Draft Event
// ---------------------------------------------------------------------------

export interface DraftEvent {
  /** The DS driver who made the challenge. */
  dsDriverId: string;
  /** The man he beat, whose place he takes. */
  targetDriverId: string;
  /** The car immediately behind him, carried along. May be absent. */
  dmdDriverId: string | null;
  /** A second DS driver caught in the same event brings a third car. */
  secondDsDriverId: string | null;
  extraDmdDriverId: string | null;
  /** 2 normally, 3 when a second DS driver is involved. */
  carsMoved: number;
  ruleCitation: string;
}

/**
 * Build the Draft Event from a won challenge.
 *
 * `grid` must be the running order at the moment the challenge resolved, index
 * 0 = leader. Nothing here mutates it; the caller applies the movement.
 *
 * THE BOTTOM-GROUP LIMIT. EZRA allows a DE on a Bottom Group Churn but says the
 * DS "cannot extend beyond the Bottom Group" — so a draft that starts in the
 * bottom group may not drag its cars up out of it. `bottomGroupFrom` is the
 * first index of the bottom group; pass it and the event is clamped.
 */
export function draftEvent(input: {
  grid: readonly Car[];
  dsDriverId: string;
  targetDriverId: string;
  /** First grid index belonging to the BOTTOM group, if the DS driver is in it. */
  bottomGroupFrom?: number;
}): DraftEvent | null {
  const { grid, dsDriverId, targetDriverId } = input;

  const dsIndex = grid.findIndex((c) => c.card.driverId === dsDriverId);
  const targetIndex = grid.findIndex((c) => c.card.driverId === targetDriverId);
  if (dsIndex < 0 || targetIndex < 0) return null;

  // "The first car on the grid behind the DS driver becomes a draft-mated
  // driver (DMD)". Behind means further back in running order.
  const behind = grid[dsIndex + 1] ?? null;

  // The bottom-group clamp: a DMD that would be dragged out of the bottom group
  // is not carried. EZRA is explicit that the DS cannot extend beyond it.
  const from = input.bottomGroupFrom;
  const dsInBottom = from !== undefined && dsIndex >= from;
  const dmd = behind && (!dsInBottom || targetIndex >= from!) ? behind : null;

  // "If two or more DS drivers are in the same DE, three cars will move: the
  // lead DS, the mated DS and an additional DMD immediately behind the second
  // DS." The mated DS is the DMD, when the DMD himself carries the trait.
  const secondIsDs = dmd !== null && hasDS(dmd.card);
  const extra = secondIsDs ? (grid[dsIndex + 2] ?? null) : null;

  return {
    dsDriverId,
    targetDriverId,
    dmdDriverId: dmd?.card.driverId ?? null,
    secondDsDriverId: secondIsDs ? (dmd?.card.driverId ?? null) : null,
    extraDmdDriverId: extra?.card.driverId ?? null,
    carsMoved: secondIsDs && extra ? DE_CARS_TWO_DS : dmd ? DE_CARS_NORMAL : 1,
    ruleCitation: secondIsDs ? CITE_DRAFT_TWO_DS : CITE_DRAFT_DE,
  };
}

/**
 * Apply a Draft Event to a running order, returning the new one.
 *
 * The DS driver takes the defeated target's place. The DMD — and, when a second
 * DS is involved, the extra car behind him — slot in immediately behind. Every
 * displaced car shuffles back, which is the base game's own "fill the gap"
 * behaviour rather than anything this module invents.
 *
 * Pure: the input array is not touched.
 */
export function applyDraftEvent(grid: readonly Car[], de: DraftEvent): Car[] {
  const movingIds = [de.dsDriverId, de.dmdDriverId, de.extraDmdDriverId].filter(
    (id): id is string => id !== null,
  );
  // Only the cars the event actually moves — `carsMoved` is authoritative.
  const moving = movingIds.slice(0, de.carsMoved);

  const targetIndex = grid.findIndex((c) => c.card.driverId === de.targetDriverId);
  if (targetIndex < 0) return [...grid];

  const movers = moving
    .map((id) => grid.find((c) => c.card.driverId === id))
    .filter((c): c is Car => c !== undefined);
  if (movers.length === 0) return [...grid];

  const rest = grid.filter((c) => !moving.includes(c.card.driverId));
  // Where the target sits once the movers are lifted out.
  const insertAt = rest.findIndex((c) => c.card.driverId === de.targetDriverId);
  if (insertAt < 0) return [...grid];

  const out = [...rest.slice(0, insertAt), ...movers, ...rest.slice(insertAt)];
  return out.map((c, i) => ({ ...c, position: i + 1 }));
}

/**
 * A readable sentence for the ledger, built only from what happened.
 *
 * Named drivers are passed in rather than looked up, so this module never needs
 * to know how names are stored and can never print one it was not given.
 */
export function describeDraft(
  de: DraftEvent,
  nameOf: (id: string) => string,
  quality: Quality,
): string {
  const ds = nameOf(de.dsDriverId);
  const target = nameOf(de.targetDriverId);
  if (de.carsMoved === 1) {
    return `${ds} gets the run on ${target} — a ${quality} challenge with the draft, and nobody behind him to bring along.`;
  }
  if (de.secondDsDriverId) {
    return `${ds} draws ${nameOf(de.secondDsDriverId)} and ${nameOf(de.extraDmdDriverId ?? '')} with him past ${target} — two drafting cars in the same line and three of them move.`;
  }
  return `${ds} clears ${target} with the draft and tows ${nameOf(de.dmdDriverId ?? '')} through behind him.`;
}

/**
 * Drivers in the bottom group's lap-down sub-group cannot draft.
 *
 * The 2016 sheet says so outright — "Lap down Cars cannot join a Draft" — and
 * EZRA's own LDSG is the structure that makes it meaningful. Exposed separately
 * because the lap-down split is a different add-on that may or may not be on.
 */
export const canDraftWhileLapped = (car: Pick<Car, 'lapsDown'>): boolean => car.lapsDown !== true;

/** Every DS driver currently running, in running order. */
export const dsDriversIn = (state: Pick<RaceState, 'grid'>): Car[] =>
  state.grid.filter((c) => hasDS(c.card) && c.status === 'running');
